/* eslint-disable */

import easing from '../shaderlib/easing.glsl.js'
import noise from '../shaderlib/noise.glsl.js'
import rangeTranslation from '../shaderlib/rangeTranslation.glsl.js'

export default `
// -----------------------------------------------------------------------------
//
// Displaces shape using audio data, ambient Perlin noise patterns, and
// overlapping sine waves.
//
// -----------------------------------------------------------------------------

struct SineWave {
    float count;
    float speed;
    float amplitude;
};

uniform int uData[128];
uniform float time;
uniform float timeDelta;
uniform float timeMult;
uniform float zoom;
uniform SineWave wavesX[3];
uniform SineWave wavesY[3];
uniform float jitterOffset0;
uniform float jitterOffset1;
uniform float fakeDataFade;
uniform float fakeDataFadeOffset;
uniform highp sampler2D uTexture;

varying float amount;
varying vec2 vUv;
varying vec2 jitter;
varying float gapNoise;

${easing}
${noise}
${rangeTranslation}

float normalizeFreq(float freq) {
    return (freq + 100.0) / 70.0;
}

float normalizeFreq(int freq) {
    return normalizeFreq(float(freq));
}

float normalizeFreq(uint freq) {
    return normalizeFreq(float(freq));
}

float waveOffset(float pos, SineWave wave) {
    return sin((pos + timeDelta * wave.speed) * PI2 * wave.count) * wave.amplitude;
}

// Returns a vec2 that is x and y rotated by the value of degrees
vec2 rotateBy(float degrees, float x, float y) {
    float angle = degrees * PI / 180.0;
    return vec2(
        x * cos(angle) - y * sin(angle),
        x * sin(angle) + y * cos(angle)
    );
}

// Creates a field of overlapping noise used when no sound data is being passed
// into this program, but it is still being rendered to the scene
float generateFakeData() {
    float fakeData0 = easeInOutExpo(translateNoise3d(cnoise(vec3(
        (uv.x + fakeDataFadeOffset) * 10.0,
        ((uv.y + fakeDataFadeOffset) * 1.0) - (timeDelta * 0.02),
        fakeDataFadeOffset
    )))) * 0.5;

    float fakeData1 = easeInOutSine(translateNoise3d(cnoise(vec3(
        (uv.x + fakeDataFadeOffset) * 20.0,
        ((uv.y + fakeDataFadeOffset) * 20.0) - (timeDelta * 6.0),
        fakeDataFadeOffset
    )))) * 0.07;

    float fakeData2 = easeInOutSine(translateNoise3d(cnoise(vec3(
        (uv.x + fakeDataFadeOffset) * 4.0,
        ((uv.y + fakeDataFadeOffset) * 10.0) - (timeDelta * 4.0),
        fakeDataFadeOffset
    )))) * 0.06;

    return (fakeData0 + fakeData1 + fakeData2) * uv.y;
}

void main() {
    // Mirrors UV along vertical axis and normalizes back to [0.0, 1.0]
    vec2 mirroredUv = (uv - vec2(0.5, 0.0)) * vec2(2.0, 1.0);

    // Creates a stretched Perlin noise field used to create gaps in the mesh
    // and also blend between colors.
    gapNoise = easeInOutQuart(translateNoise3d(cnoise(vec3(
        (timeMult + uv.x) * float(SHAPE_WIDTH) * 0.5,
        (timeMult + uv.y) * float(SHAPE_DEPTH) * 1.5,
        timeDelta * 0.15
    ))));

    // Skews the y-coord of the uv to stretch out the bottom portion
    float skewedUvY = max(0.0, translate(
         0.0,   1.0,
        -0.4,   1.0,
        uv.y
    ));

    // Get the amount of frequency offset for this vertex
    amount = normalizeFreq(texture(uTexture, vec2(mirroredUv.x * zoom, skewedUvY)).r);

    // Mix the frequency data with fake data
    amount = mix(amount, generateFakeData(), fakeDataFade);

    // Create ambient wave pattern
    vec2 rotated0 = rotateBy(0.0, uv.x, uv.y);
    vec2 rotated1 = rotateBy(10.0, uv.x, uv.y);
    vec2 rotated2 = rotateBy(-10.0, uv.x, uv.y);
    float waveOffsetX0 = waveOffset(rotated0.x, wavesX[0]);
    float waveOffsetX1 = waveOffset(rotated1.x, wavesX[1]);
    float waveOffsetX2 = waveOffset(rotated2.x, wavesX[2]);
    float waveOffsetY0 = waveOffset(rotated0.y, wavesX[0]);
    float waveOffsetY1 = waveOffset(rotated1.y, wavesX[1]);
    float waveOffsetY2 = waveOffset(rotated2.y, wavesX[2]);
    float waveTotal = waveOffsetX0 + waveOffsetX1 + waveOffsetX2 + waveOffsetY0 + waveOffsetY1 + waveOffsetY2;
    float wave = waveTotal / (wavesX[0].amplitude + wavesX[1].amplitude + wavesX[2].amplitude + wavesY[0].amplitude + wavesY[1].amplitude + wavesY[2].amplitude);
    wave = translate(-1.0, 1.0, -0.4, 1.0, wave);

    // Create x,y jitter offset
    float noiseZoom = 3.0;
    jitter = vec2(
        translateNoise4d(cnoise(vec4(
            position.x * noiseZoom,
            position.y * noiseZoom,
            jitterOffset0,
            timeDelta * 0.07
        ))),
        translateNoise4d(cnoise(vec4(
            position.x * noiseZoom,
            position.y * noiseZoom,
            jitterOffset1,
            timeDelta * 0.07
        )))
    );

    float waveOffset = wave * uv.y * 0.3;
    float soundOffset = amount * (1.0 - uv.y) * 1.5 * min(1.0, uv.y * 8.0);

    // Set local position of vertex
    vec3 pos = vec3(
        position.x + (jitter.x * 0.1),
        position.y + (jitter.y * 0.1),
        position.z + waveOffset + soundOffset
    );

    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);

    vUv = uv;
}
`;
